import React, { useEffect, useState } from 'react';
import { basicService, passwordManagerPolicyService } from '@app/services';
import { Grid, Typography, Box } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import { useTypedTranslation } from '@app/hooks';
import { handleSetSnackbar } from '@app/store/actions/snackbar.actions';
import { useParams } from 'react-router';
import { FormMode } from '@app/helpers/enums';
import { PasslySwitch, PageActions, PasslyTextField } from '@app/components';
import { defaultOrgId, genericErrorToast, translationVariable } from '@app/helpers';
import { setExceptionHandler, addDetailsBreadcrumb } from '@app/store/actions';
import { IPasswordServerPolicy, IRequirementSwitch, RequirementTypes } from '@app/models';
import { InputNumber } from '@itglue/kaseya-ui';

const AddEditPasswordServerPolicyPage: React.SFC = () => {
    const { policyId } = useParams<{ policyId: string }>();
    const viewType = policyId === '' || policyId === undefined ? FormMode.Add : FormMode.Edit;
    const minLength: number = 1;
    const maxLength: number = 256; // max supported by Active Directory, so why not us too?
    const expireAfterDaysMax: number = 9999;
    const expireAfterDaysMin: number = 0;

    const [requirementsSwitches, setRequirementsSwitches] = useState({
        uppercase: true,
        lowercase: true,
        digits: true,
        specials: true
    });

    const [displayName, setDisplayName] = useState<string>('');
    const [minimumLength, setMinimumLength] = useState<number>(minLength);
    const [maximumLength, setMaximumLength] = useState<number>(maxLength);
    const [expireAfter, setExpireAfter] = useState<number>(minLength);
    const [error, setError] = useState<boolean>(false);
    const [loadedDisplayName, setLoadedDisplayName] = useState<string>('');

    const requirementsSwitchesData: IRequirementSwitch[] = [
        {
            type: RequirementTypes.uppercase,
            checked: requirementsSwitches.uppercase,
            label: 'REQUIRE_UPPERCASE'
        },
        {
            type: RequirementTypes.lowercase,
            checked: requirementsSwitches.lowercase,
            label: 'REQUIRE_LOWERCASE'
        },
        {
            type: RequirementTypes.digits,
            checked: requirementsSwitches.digits,
            label: 'REQUIRE_DIGITS'
        },
        {
            type: RequirementTypes.specials,
            checked: requirementsSwitches.specials,
            label: 'REQUIRE_SYMBOL'
        }
    ];

    const dispatch = useDispatch();
    const { translation } = useTypedTranslation();

    useEffect(() => {
        dispatch(setExceptionHandler(true));

        if (viewType === FormMode.Edit) {
            passwordManagerPolicyService.getPolicy(policyId, genericErrorToast, handleGetPolicySuccess);
        }
    }, []);

    useEffect(() => {
        const parsedMin = parseInt(minimumLength.toString());
        const parsedMax = parseInt(maximumLength.toString());

        if (
            displayName !== '' &&
            expireAfter.toString() !== '' &&
            !outOfLengthRange(parsedMin) &&
            !outOfLengthRange(parsedMax) &&
            parsedMin <= parsedMax &&
            !outOfExpirationRange(expireAfter)
        ) {
            setError(false);
        } else {
            setError(true);
        }
    }, [minimumLength, maximumLength, displayName, expireAfter]);

    const handleGetPolicySuccess = (response: any) => {
        const results: IPasswordServerPolicy = response.data;

        if (results) {
            setDisplayName(results.DisplayName);
            setMinimumLength(results.MinimumLength);
            setMaximumLength(results.MaximumLength);
            setExpireAfter(results.ExpireEveryXDays);
            setLoadedDisplayName(results.DisplayName);
            setRequirementsSwitches({
                ...requirementsSwitches,
                [RequirementTypes.lowercase]: results.RequireLowercase,
                [RequirementTypes.uppercase]: results.RequireUppercase,
                [RequirementTypes.digits]: results.RequireDigits,
                [RequirementTypes.specials]: results.RequireSpecials
            });

            dispatch(addDetailsBreadcrumb(results.DisplayName));
        }
    };

    const onFormChange = (e: any) => {
        let { name, value } = e.target;

        switch (name) {
            case 'displayName':
                setDisplayName(value);
                break;
            case 'minimumLength':
                setMinimumLength(value);
                break;
            case 'maximumLength':
                setMaximumLength(value);
                break;
            case 'expireAfter':
                setExpireAfter(value);
                break;
            default:
                break;
        }
    };

    const handleChange = (name: RequirementTypes, value: boolean) => {
        setRequirementsSwitches({ ...requirementsSwitches, [name]: !value });
    };

    const handleSubmit = () => {
        let jsonObject: any = {};

        jsonObject.OrganizationId = defaultOrgId;
        jsonObject.DisplayName = displayName.trim();
        jsonObject.MinimumLength = minimumLength;
        jsonObject.MaximumLength = maximumLength;
        jsonObject.ExpireEveryXDays = expireAfter;
        jsonObject.EnableAutoRekey = false; // not for version 1
        jsonObject.RekeyEveryXDays = 1; // not for version 1
        jsonObject.RequireUppercase = requirementsSwitches.uppercase;
        jsonObject.RequireLowercase = requirementsSwitches.lowercase;
        jsonObject.RequireDigits = requirementsSwitches.digits;
        jsonObject.RequireSpecials = requirementsSwitches.specials;
        jsonObject.SaveHistory = false; // not for version 1
        jsonObject.EnforceHistory = false; // not for version 1
        jsonObject.RememberLastXPasswords = 1; // not for version 1

        if (viewType === FormMode.Add) {
            passwordManagerPolicyService.addPolicy(jsonObject, genericErrorToast, handleSuccess);
        } else {
            passwordManagerPolicyService.editPolicy(jsonObject, policyId, genericErrorToast, handleSuccess);
        }
    };

    const handleSuccess = (response: any) => {
        let msg: string = '';

        if (viewType === FormMode.Add) {
            msg = translation('SUCCESS_ADD_PASSWORD_SERVER_POLICY').replace(translationVariable, displayName);
        } else {
            msg = translation('SUCCESS_EDIT_PASSWORD_SERVER_POLICY').replace(translationVariable, displayName);
        }

        dispatch(handleSetSnackbar({ show: true, message: msg, severity: 'success' }));

        handleCancel();
    };

    const handleCancel = () => {
        basicService.redirect('/passwordserver/passwordserverpolicy');
    };

    const outOfLengthRange = (currentLength: number): boolean => {
        return currentLength < minLength || currentLength > maxLength || isNaN(currentLength);
    };

    const outOfExpirationRange = (expiration: number): boolean => {
        const ooer = expiration < expireAfterDaysMin || expiration > expireAfterDaysMax || isNaN(expiration);

        return ooer;
    };

    return (
        <div className='content'>
            {viewType === FormMode.Edit && (
                <Box mb={1}>
                    <Typography variant='h3'>{loadedDisplayName}</Typography>
                </Box>
            )}
            <Grid xs direction='column' container wrap='nowrap'>
                <Grid item xs={2}>
                    <PasslyTextField
                        label={translation('POLICY_NAME')}
                        id='displayName-input'
                        name='displayName'
                        onChange={onFormChange}
                        type='text'
                        value={displayName}
                        inputProps={{ maxLength: 100 }}
                        required
                        autoFocus
                        fullWidth
                    />
                </Grid>
                <Grid item>
                    <Typography variant='h6'>{translation('PASSWORD_LENGTH')}:</Typography>
                </Grid>
                <Grid item xs={2}>
                    <Grid container direction='column'>
                        <InputNumber
                            label={`${translation('MINIMUM')} ${translation('CHARACTERS')}`}
                            name='minimumLength'
                            value={minimumLength}
                            onValueChange={onFormChange}
                            showButtons
                            idecrementButtonClassName={''}
                            min={minLength}
                            max={maxLength}
                            errorMessage={
                                parseInt(minimumLength.toString()) > parseInt(maximumLength.toString())
                                    ? translation('PASSWORD_SERVER_POLICY_MAXIMUM_LENGTH_ERROR')
                                    : ''
                            }
                        />
                        <InputNumber
                            label={`${translation('MAXIMUM')} ${translation('CHARACTERS')}`}
                            name='maximumLength'
                            value={maximumLength}
                            onValueChange={onFormChange}
                            showButtons
                            idecrementButtonClassName={''}
                            min={minLength}
                            max={maxLength}
                        />
                    </Grid>
                </Grid>
                <Grid item>
                    <Typography variant='h3'>{translation('EXPIRATION_TIME')}:</Typography>
                </Grid>
                <Grid item xs={2}>
                    <InputNumber
                        label={`${translation('EXPIRE_PASSWORD_AFTER')} (${translation('DAYS')})`}
                        name='expireAfter'
                        value={expireAfter}
                        onValueChange={onFormChange}
                        showButtons
                        idecrementButtonClassName={''}
                        min={expireAfterDaysMin}
                        max={expireAfterDaysMax}
                    />
                </Grid>
                <Grid item>
                    <Typography variant='h3'>{translation('REQUIREMENTS')}:</Typography>
                </Grid>
                <Grid direction='column' container spacing={2}>
                    {requirementsSwitchesData.map((requirement: IRequirementSwitch, i: number) => (
                        <Grid item key={i}>
                            <PasslySwitch
                                label={translation(requirement.label)}
                                checked={requirement.checked}
                                onChange={() => handleChange(requirement.type, requirement.checked)}
                            />
                        </Grid>
                    ))}
                </Grid>
                <PageActions
                    primaryBtn={translation('SAVE_CHANGES')}
                    secondaryBtn={translation('CANCEL')}
                    onClickPrimary={handleSubmit}
                    disabled={error}
                    onClickSecondary={handleCancel}
                />
            </Grid>
        </div>
    );
};

export default AddEditPasswordServerPolicyPage;
